import { config } from '../config/config';
import { supabase } from '../lib/supabase';

// Interface pour les détails de paiement PayPal
export interface PayPalShopPaymentDetails {
  amount: number;
  currency: string; // EUR
  order_id: string;
  order_number: string;
  description?: string;
  customerName?: string;
  customerEmail?: string;
  return_base_url?: string;
}

// Interface pour la réponse d'initiation de paiement
export interface PayPalShopPaymentResponse {
  order: {
    id: string;
    amount: number;
    currency: string;
    status: string;
    payment_method: string;
    payment_reference: string;
    payment_url: string;
  };
  payment_reference: string;
  payment_url: string;
}

// Interface pour la réponse de statut de paiement
export interface PayPalShopStatusResponse {
  status: 'success' | 'pending' | 'failed' | 'COMPLETED' | 'APPROVED' | 'PENDING' | 'VOIDED' | 'EXPIRED';
  data: any;
}

/**
 * Initie un paiement PayPal via la fonction Edge spécifique pour la boutique
 */
export const initiatePayPalShopEdgePayment = async (
  details: PayPalShopPaymentDetails
): Promise<PayPalShopPaymentResponse> => {
  try {
    console.log('[PayPalShopEdge] Initiation du paiement PayPal:', details);

    // Utiliser la fonction edge spécifique pour la boutique
    const response = await fetch(`${config.supabaseUrl}/functions/v1/shop-paypal-initiate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.supabaseKey}`,
      },
      body: JSON.stringify(details),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('[PayPalShopEdge] Erreur de la fonction edge:', errorData);
      throw new Error(errorData.error || "Échec de l'initiation du paiement PayPal");
    }

    const data = await response.json();
    console.log('[PayPalShopEdge] Paiement PayPal initié avec succès:', data);
    return data;
  } catch (error) {
    console.error("[PayPalShopEdge] Erreur lors de l'initiation du paiement:", error);
    throw error;
  }
};

/**
 * Vérifie le statut d'un paiement PayPal via la base de données pour la boutique
 */
export const checkPayPalShopEdgePaymentStatus = async (
  reference: string,
  order_id: string
): Promise<PayPalShopStatusResponse> => {
  const requestId = Math.random().toString(36).substring(2, 10);
  console.log(`[PayPalShopEdge][${requestId}] Vérification du statut - reference: ${reference}, order_id: ${order_id}`);
  
  try {
    // Vérifier le statut de la commande dans la base de données
    console.log(`[PayPalShopEdge][${requestId}] Vérification du statut dans la base de données`);
    
    const startTime = Date.now();
    const { data: order, error } = await supabase
      .from('shop_orders')
      .select('status, payment_reference, created_at')
      .eq('id', order_id)
      .single();
    
    const queryTime = Date.now() - startTime;
    console.log(`[PayPalShopEdge][${requestId}] Requête terminée en ${queryTime}ms`);
    console.log(`[PayPalShopEdge][${requestId}] Résultat:`, { 
      order: order ? {
        status: order.status,
        payment_reference: order.payment_reference,
        created_at: order.created_at
      } : null, 
      error: error ? error.message : null 
    });
    
    if (error) {
      console.error(`[PayPalShopEdge][${requestId}] Erreur lors de la récupération:`, error);
      throw new Error(`Erreur lors de la récupération de la commande: ${error.message}`);
    }
    
    if (!order) {
      console.error(`[PayPalShopEdge][${requestId}] Commande ${order_id} introuvable`);
      throw new Error('Commande introuvable');
    }
    
    console.log(`[PayPalShopEdge][${requestId}] Statut de la commande: ${order.status}`);
    
    // Vérifier si la commande est marquée comme payée
    if (order.status === 'paid') {
      console.log(`[PayPalShopEdge][${requestId}] Commande déjà payée, retour immédiat`);
      return { 
        status: 'success', 
        data: { 
          order_id,
          payment_reference: reference,
          status: 'paid',
          created_at: order.created_at
        } 
      };
    }
    
    console.log(`[PayPalShopEdge][${requestId}] Commande en attente (statut: ${order.status})`);
    
    // Retourner le statut actuel
    return {
      status: order.status === 'pending_payment' ? 'pending' : 'failed',
      data: {
        order_id,
        payment_reference: reference,
        status: order.status,
        created_at: order.created_at
      }
    };
  } catch (error) {
    console.error(`[PayPalShopEdge][${requestId}] Erreur lors de la vérification:`, error);
    throw error;
  }
};
